// src/encounter/lib/lab-order-v3.ts
// The rules behind the Add lab popup on the agreed lab model (PROD-2849). Pure functions only; the
// dialog (lab-add-dialog-v3.tsx) owns the state and the parts (lab-add-v3-parts.tsx) draw it.
//
// Design: canvas boards 6-9 (AddLabAtomic, AddLabHouse, AddLabDuplicate, EdgeStates). Data: Scrips.Master
// order-search (PROD-2839), which carries no price anywhere. Save: Scrips.Patient's A&P lab fields from
// PROD-2841 (fulfilmentMode, labPriceLineId, memberLabs[]) and PROD-2640 (aoeAnswers[]).
//
// Three types (the boards' type chip):
//   - Single test: no TESTS list; one lab.
//   - Panel: its tests are fixed ("Tests (10/10)", every box ticked and locked), one lab.
//   - Clinic panel: the clinic set the lab when it built it (one lab, or a lab per test); each test
//     opens ticked or not per `tickedByDefault`; Package or Per test billing.
import type { OrderCoding } from './order-payload';
import type { OtherLabOnPlan } from './lab-catalogue';
import { labDisplayLabel, labFullName } from './lab-display-label';
import type {
  LabOrderItemDetail,
  LabOrderLab,
  LabOrderMember,
  LabOrderQuestion,
  LabOrderSearchItem,
  LabOrderSpecimen,
} from '../services/use-lab-order-search';

export const PATIENTS_CHOICE_LABEL = "Patient's choice of lab";
export const PATIENTS_CHOICE_MENU_HINT = 'Not sent by the clinic; not billed';
export const PANEL_HINT = 'Ordered as one panel by the lab. To leave a test out, order the tests you need one by one.';
export const ALREADY_ORDERED_NOTE = 'Already ordered in this visit; drawn and billed once. Not added again.';
export const MEMBER_PATIENTS_CHOICE_NOTE =
  'No contracted lab offers it: the patient takes this test to any lab; not billed by the clinic.';
export const NOT_TICKED_NOTE = 'Not ticked by default.';
export const LEFT_OUT_NOTE = 'Left out.';
export const CLINIC_DOES_NOT_SEND_LINE =
  "This clinic doesn't send tests to labs. The patient takes the printed order to any lab and pays the lab. Nothing goes on the clinic invoice.";
export const PATIENTS_CHOICE_LINE =
  'Not sent or billed by the clinic. It prints on the order sheet in wrap-up for the patient to take to any lab.';
export const NO_CLINIC_MATCH = "No match in your clinic's tests and panels";
export const NOT_OFFERED_BY_CLINIC = 'not offered by your clinic';

export type FulfilmentMode = 'ContractedLab' | 'PatientChoice';

const same = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

export const isPanelType = (type?: string | null) => same(type, 'Panel');
export const isClinicPanelType = (type?: string | null) => same(type, 'Clinic panel') || same(type, 'ClinicPanel');
export const isSingleTestType = (type?: string | null) => !isPanelType(type) && !isClinicPanelType(type);

/** The type chip's label, in design copy. */
export function typeLabel(type?: string | null): string {
  if (isPanelType(type)) return 'Panel';
  if (isClinicPanelType(type)) return 'Clinic panel';
  return 'Single test';
}

/** Panel is blue (board 6); Single test and Clinic panel are grey (boards 7-9). */
export function typeTone(type?: string | null): 'blue' | 'grey' {
  return isPanelType(type) ? 'blue' : 'grey';
}

// ── The lab ─────────────────────────────────────────────────────────────────────────────────────

/** The lab the order goes to: one of the allowed labs, or Patient's choice of lab. */
export type LabPick = { kind: 'lab'; lab: LabOrderLab } | { kind: 'patients-choice' };

/** The lab's label ("Unilabs", "NRL"): its name when 20 characters or fewer, else its code (PROD-3116). */
export const labName = (lab?: LabOrderLab | null) => labDisplayLabel(lab);

/** The lab's full name, for the tooltip on its label. */
export const labTitle = (lab?: LabOrderLab | null) => labFullName(lab);

/** A clinic panel member's lab, in the shape the label helper reads (the member's own `name` is the test's). */
const memberLab = (m: LabOrderMember) => ({ name: m.effectiveLabName, shortCode: m.effectiveLabShortCode });

/**
 * The labs a clinic panel's contracted members go to, once each (matched on the vendor id, else the
 * label): labels and full names, same order.
 */
function memberLabs(members: readonly LabOrderMember[]): { labels: string[]; titles: string[] } {
  const seen = new Map<string, { label: string; title: string }>();
  for (const m of members) {
    if (m.isPatientsChoice) continue;
    const label = labDisplayLabel(memberLab(m));
    const key = m.effectiveLabVendorId?.trim() || `label:${label}`;
    if (label && !seen.has(key)) seen.set(key, { label, title: labFullName(memberLab(m)) });
  }
  const labs = [...seen.values()];
  return { labels: labs.map((l) => l.label), titles: labs.map((l) => l.title) };
}

/** The clinic does not send tests to labs (organization switch off): everything is Patient's choice. */
export function clinicDoesNotSendTests(item: LabOrderItemDetail | LabOrderSearchItem): boolean {
  if (same(item.patientsChoiceReason, 'ClinicDoesNotSendTests')) return true;
  const d = item as LabOrderItemDetail;
  return d.switchesAvailable === true && d.sendsTestsToLabs === false;
}

/** The lab the popup opens on: Master's default lab, else Patient's choice of lab. */
export function defaultPick(item: LabOrderSearchItem): LabPick {
  if (clinicDoesNotSendTests(item) || item.isPatientsChoice || !item.defaultLab) return { kind: 'patients-choice' };
  return { kind: 'lab', lab: item.defaultLab };
}

/**
 * The lab chip's menu (board 6): the allowed labs, then "Patient's choice of lab". Null = the chip is
 * fixed: a clinic panel (the clinic set the lab), `canChangeLab` false, or the clinic doesn't send tests.
 */
export function labMenu(item: LabOrderItemDetail | LabOrderSearchItem): LabOrderLab[] | null {
  if (isClinicPanelType(item.type) || !item.canChangeLab || clinicDoesNotSendTests(item)) return null;
  return item.allowedLabs ?? [];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The members a physician can tick (or that are fixed), skipping reflex tests. */
function orderableMembers(detail: LabOrderItemDetail | null | undefined): LabOrderMember[] {
  return (detail?.members ?? []).filter((m) => !m.isReflex);
}

/** A clinic panel's labs for the chip: its members' labs, else its default lab. */
function clinicPanelLabs(item: LabOrderItemDetail | LabOrderSearchItem): { labels: string[]; titles: string[] } {
  const own = memberLabs(orderableMembers(item as LabOrderItemDetail));
  if (own.labels.length > 0) return own;
  return labName(item.defaultLab) ? { labels: [labName(item.defaultLab)], titles: [labTitle(item.defaultLab)] } : own;
}

/** The lab chip's text: "NRL", "Patient's choice of lab", or a clinic panel's "NRL · 1 test Patient's choice". */
export function labChipText(item: LabOrderItemDetail | LabOrderSearchItem, pick: LabPick): string {
  if (isClinicPanelType(item.type)) {
    if (clinicDoesNotSendTests(item)) return PATIENTS_CHOICE_LABEL;
    const members = orderableMembers(item as LabOrderItemDetail);
    const names = clinicPanelLabs(item).labels;
    const pc = members.length > 0 ? members.filter((m) => m.isPatientsChoice).length : (item.patientsChoiceTestCount ?? 0);
    if (names.length === 0) return PATIENTS_CHOICE_LABEL;
    return pc > 0 ? `${names.join(' · ')} · ${plural(pc, 'test')} Patient's choice` : names.join(' · ');
  }
  return pick.kind === 'lab' ? labName(pick.lab) || 'Lab' : PATIENTS_CHOICE_LABEL;
}

/** The lab chip's tooltip: the full name of each lab it names; undefined for Patient's choice of lab. */
export function labChipTitle(item: LabOrderItemDetail | LabOrderSearchItem, pick: LabPick): string | undefined {
  if (isClinicPanelType(item.type)) {
    if (clinicDoesNotSendTests(item)) return undefined;
    return clinicPanelLabs(item).titles.join(' · ') || undefined;
  }
  return pick.kind === 'lab' ? labTitle(pick.lab) || undefined : undefined;
}

// ── Header lines and chips ──────────────────────────────────────────────────────────────────────

/**
 * The code line under the name:
 *   Single test / Panel: "19040 · LOINC 24362-6 · CPT 80069"; no LOINC and no CPT: "2224015 · clinic code".
 *   Clinic panel: "9024017 · clinic panel · v3 · lab set by the clinic" (Package, board 7) or
 *                 "9024010 · clinic panel · billed per test" (board 8).
 */
export function codeLine(item: LabOrderSearchItem): string {
  const code = (item.clinicCode || item.code || '').trim();
  if (isClinicPanelType(item.type)) {
    const tail = same(item.billingMode, 'PerTest')
      ? ['billed per test']
      : [typeof item.version === 'number' ? `v${item.version}` : '', 'lab set by the clinic'];
    return [code, 'clinic panel', ...tail].filter(Boolean).join(' · ');
  }
  const loinc = item.loinc?.trim();
  const cpt = item.cptCode?.trim();
  if (!loinc && !cpt) return code ? `${code} · clinic code` : '';
  return [code, loinc ? `LOINC ${loinc}` : '', cpt ? `CPT ${cpt}` : ''].filter(Boolean).join(' · ');
}

const specimenText = (s: LabOrderSpecimen, withVolume: boolean) => {
  const head = [s.specimenType?.trim(), s.container?.trim()].filter(Boolean).join(' · ');
  const vol = withVolume ? s.minVolume?.trim() : '';
  return [head, vol].filter(Boolean).join(' ');
};

/**
 * The chip row: specimen ("Serum · SST 1 mL"; a clinic panel's distinct specimens, "· 1 draw" when there
 * is one) and the prep ("Fasting 8 h preferred"). Patient's choice of lab shows no specimen: tubes and
 * volumes are the lab's, and the patient's lab is not known (board 9).
 */
export function chipRow(item: LabOrderSearchItem, patientsChoice: boolean): string[] {
  const chips: string[] = [];
  if (isClinicPanelType(item.type)) {
    const specs = [...new Set((item.specimens ?? []).map((s) => specimenText(s, false)).filter(Boolean))];
    if (specs.length === 1) chips.push(`${specs[0]} · 1 draw`);
    else chips.push(...specs);
  } else if (!patientsChoice && item.specimen) {
    const s = specimenText(item.specimen, true);
    if (s) chips.push(s);
  }
  const prep = prepText(item.prepType, item.prepNote);
  if (prep) chips.push(prep);
  return chips;
}

/** The prep chip: the test's prep note, else "Fasting" / "Timed"; none for no prep. */
export function prepText(prepType?: string | null, prepNote?: string | null): string | null {
  const note = prepNote?.trim();
  if (note) return note;
  if (same(prepType, 'Fasting')) return 'Fasting';
  if (same(prepType, 'Timed')) return 'Timed';
  return null;
}

// ── Tests ───────────────────────────────────────────────────────────────────────────────────────

export type V3RowState = 'fixed' | 'tick' | 'already' | 'reflex';

export interface V3TestRow {
  key: string;
  name: string;
  loinc?: string;
  state: V3RowState;
  checked: boolean;
  /** The note under the name ("Left out.", "Not ticked by default.", ...). */
  note?: string;
  /** Left out of a clinic panel that ticks it by default: the note carries an Undo. */
  undo?: boolean;
  /** A nested Panel inside a clinic panel: "Panel · 10 tests, fixed". */
  subLine?: string;
  /** A clinic panel with a lab per test: the row's lab label (ticked rows only). */
  labName?: string;
  /** That lab's full name, for the tooltip. */
  labTitle?: string;
  /** The member goes as Patient's choice of lab (the amber tag). */
  patientsChoice?: boolean;
}

/** The selection key of a member. */
export const memberKey = (m: LabOrderMember) => m.memberId;

/** A member's coding as the save sends it (Patient matches memberLabs and omissions by code + system). */
export function memberCoding(m: LabOrderMember): OrderCoding {
  const code = (m.code || m.loinc || m.clinicCode || m.name).trim();
  return { code, displayName: m.name, ...(m.system?.trim() ? { system: m.system.trim() } : {}) };
}

/** Members already on another lab order of this visit (matched on code or LOINC). */
export function alreadyOrderedKeys(detail: LabOrderItemDetail | null | undefined, otherLabs: readonly OtherLabOnPlan[] | undefined): Set<string> {
  const codes = new Set((otherLabs ?? []).flatMap((o) => o.codes.map((c) => c.trim().toLowerCase())).filter(Boolean));
  const out = new Set<string>();
  if (!isClinicPanelType(detail?.type) || codes.size === 0) return out;
  for (const m of orderableMembers(detail)) {
    const hit = [m.code, m.loinc].some((c) => c && codes.has(c.trim().toLowerCase()));
    if (hit) out.add(memberKey(m));
  }
  return out;
}

/**
 * The ticks the popup opens with. A Panel: every test (fixed). A clinic panel: `tickedByDefault`, or on
 * an edit the saved selection (matched on the member's code).
 */
export function initialTicks(detail: LabOrderItemDetail, savedSubTests?: readonly OrderCoding[]): Set<string> {
  const members = orderableMembers(detail);
  if (!isClinicPanelType(detail.type)) return new Set(members.map(memberKey));
  if (savedSubTests) {
    const saved = new Set(savedSubTests.map((t) => t.code.trim().toLowerCase()));
    return new Set(members.filter((m) => saved.has(memberCoding(m).code.toLowerCase())).map(memberKey));
  }
  return new Set(members.filter((m) => m.tickedByDefault !== false).map(memberKey));
}

/** Clinic panel members lab: shown on the rows only when the panel has more than one lab (or Patient's choice). */
function hasLabPerTest(members: readonly LabOrderMember[]): boolean {
  const labs = new Set(members.map((m) => (m.isPatientsChoice ? '∅' : m.effectiveLabVendorId ?? '')));
  return labs.size > 1;
}

export function buildRows(detail: LabOrderItemDetail, ticked: ReadonlySet<string>, already: ReadonlySet<string>): V3TestRow[] {
  const clinic = isClinicPanelType(detail.type);
  const perTest = clinic && hasLabPerTest(orderableMembers(detail));
  const wholeOrderPc = clinicDoesNotSendTests(detail);
  return (detail.members ?? []).map((m) => {
    const key = memberKey(m);
    const base = { key, name: m.name, loinc: m.loinc?.trim() || undefined };
    if (m.isReflex) return { ...base, state: 'reflex' as const, checked: false, note: reflexText(m.reflexRule) };
    if (already.has(key)) return { ...base, state: 'already' as const, checked: true, note: ALREADY_ORDERED_NOTE };
    if (!clinic || m.fixed) return { ...base, state: 'fixed' as const, checked: true };
    const checked = ticked.has(key);
    const nested = (m.members ?? []).filter((n) => !n.isReflex).length;
    const pc = !!m.isPatientsChoice;
    const row: V3TestRow = {
      ...base,
      state: 'tick',
      checked,
      ...(isPanelType(m.type) ? { subLine: `Panel · ${plural(nested, 'test')}, fixed` } : {}),
      ...(pc && !wholeOrderPc ? { patientsChoice: true } : {}),
      ...(perTest && checked && !pc && labDisplayLabel(memberLab(m))
        ? { labName: labDisplayLabel(memberLab(m)), labTitle: labFullName(memberLab(m)) }
        : {}),
    };
    if (!checked && m.tickedByDefault === false) return { ...row, note: NOT_TICKED_NOTE };
    if (!checked) return { ...row, note: LEFT_OUT_NOTE, undo: true };
    if (pc && !wholeOrderPc) return { ...row, note: MEMBER_PATIENTS_CHOICE_NOTE };
    return row;
  });
}

function reflexText(rule?: string | null): string {
  const r = rule?.trim().replace(/\.$/, '');
  return r ? `Added by the lab if ${r}. Billed only then.` : 'Added by the lab. Billed only then.';
}

export interface V3Counts {
  /** Ticked, not already ordered. */
  ticked: number;
  /** Tickable, not already ordered. */
  selectable: number;
  already: number;
  /** Ticked members going as Patient's choice of lab. */
  patientsChoiceTicked: number;
  /** Ticked members the clinic sends (and bills). */
  contractedTicked: number;
  anyUntickedByDefault: boolean;
}

export function counts(detail: LabOrderItemDetail, ticked: ReadonlySet<string>, already: ReadonlySet<string>): V3Counts {
  const members = orderableMembers(detail).filter((m) => !already.has(memberKey(m)));
  const on = members.filter((m) => ticked.has(memberKey(m)));
  return {
    ticked: on.length,
    selectable: members.length,
    already: already.size,
    patientsChoiceTicked: on.filter((m) => m.isPatientsChoice).length,
    contractedTicked: on.filter((m) => !m.isPatientsChoice).length,
    anyUntickedByDefault: members.some((m) => m.tickedByDefault === false),
  };
}

/** "Tests (10/10)", "Tests (3/4)", or "Tests (3 new · 1 already ordered · 1 not ticked)". */
export function testsHeader(c: V3Counts): string {
  if (c.already > 0 || c.anyUntickedByDefault) {
    const parts = [`${c.ticked} new`];
    if (c.already > 0) parts.push(`${c.already} already ordered`);
    if (c.selectable - c.ticked > 0) parts.push(`${c.selectable - c.ticked} not ticked`);
    return `Tests (${parts.join(' · ')})`;
  }
  return `Tests (${c.ticked}/${c.selectable})`;
}

/** The line under the TESTS box. */
export function testsHint(detail: LabOrderItemDetail, c: V3Counts): string | null {
  if (isPanelType(detail.type)) return PANEL_HINT;
  if (!isClinicPanelType(detail.type)) return null;
  if (same(detail.billingMode, 'PerTest')) {
    const pc = c.patientsChoiceTicked;
    return pc > 0
      ? `Billed per test: each ticked test bills as its own Service. The order prints from wrap-up, including the Patient's choice ${pc === 1 ? 'test' : 'tests'}.`
      : 'Billed per test: each ticked test bills as its own Service.';
  }
  if (c.ticked < c.selectable) {
    return `Billed test by test, because a test is left out: the package price applies only when all ${c.selectable} are ordered.`;
  }
  return null;
}

// ── The info line ("Sent by the clinic to NRL ...") ────────────────────────────────────────────

/** `lead` + bold `lab` + `tail`, or just `lead` when there is no lab to name. */
export interface InfoLine {
  lead: string;
  lab?: string;
  /** The full name of each lab in `lab`, for the tooltip. */
  labTitle?: string;
  tail?: string;
}

const joinAnd = (names: readonly string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export function infoLine(detail: LabOrderItemDetail, pick: LabPick, c: V3Counts | null): InfoLine | null {
  if (clinicDoesNotSendTests(detail)) return { lead: CLINIC_DOES_NOT_SEND_LINE };
  if (isClinicPanelType(detail.type)) {
    // Per test (board 8): the TESTS hint says how it bills; there is no info line.
    if (same(detail.billingMode, 'PerTest') || !c) return null;
    const labs = memberLabs(orderableMembers(detail));
    if (labs.labels.length === 0 || c.contractedTicked === 0) return { lead: PATIENTS_CHOICE_LINE };
    const tail = c.ticked < c.selectable ? `. ${plural(c.contractedTicked, 'test')} on the clinic invoice.` : '. The clinic bills it as one package.';
    return { lead: 'Sent by the clinic to ', lab: joinAnd(labs.labels), labTitle: joinAnd(labs.titles), tail };
  }
  if (pick.kind === 'patients-choice') return { lead: PATIENTS_CHOICE_LINE };
  const others = (detail.allowedLabs ?? []).filter((l) => l.labVendorId !== pick.lab.labVendorId).map(labName).filter(Boolean);
  const preferred = !!pick.lab.isPreferred;
  const paren = preferred
    ? others.length > 0
      ? ` (preferred lab; ${joinAnd(others)} also ${others.length === 1 ? 'offers' : 'offer'} it)`
      : ' (preferred lab)'
    : '';
  const bills = isPanelType(detail.type) ? ' The clinic bills it as one panel.' : '';
  return { lead: 'Sent by the clinic to ', lab: labName(pick.lab), labTitle: labTitle(pick.lab) || undefined, tail: `${paren}.${bills}` };
}

// ── Questions from the lab ──────────────────────────────────────────────────────────────────────

export const questionKey = (q: LabOrderQuestion) => (q.code?.trim() || q.text.trim()).toLowerCase();

/**
 * The questions to ask: the item's own (a single test or Panel), or a clinic panel's ticked tests'
 * (one per code). None for an order the clinic does not send: the questions go to the lab.
 */
export function questionsFor(detail: LabOrderItemDetail, ticked: ReadonlySet<string>, already: ReadonlySet<string>, pick: LabPick): LabOrderQuestion[] {
  const out = new Map<string, LabOrderQuestion>();
  const add = (qs?: LabOrderQuestion[] | null) => {
    for (const q of qs ?? []) if (q?.text?.trim() && !out.has(questionKey(q))) out.set(questionKey(q), q);
  };
  if (clinicDoesNotSendTests(detail)) return [];
  if (isClinicPanelType(detail.type)) {
    const walk = (ms?: LabOrderMember[] | null) => {
      for (const m of ms ?? []) {
        add(m.questions);
        walk(m.members);
      }
    };
    for (const m of orderableMembers(detail)) {
      if (!ticked.has(memberKey(m)) || already.has(memberKey(m)) || m.isPatientsChoice) continue;
      add(m.questions);
      walk(m.members);
    }
  } else {
    if (pick.kind === 'patients-choice') return [];
    add(detail.questions);
  }
  return [...out.values()].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

/** Required questions still unanswered. */
export function unansweredRequired(questions: readonly LabOrderQuestion[], answers: Readonly<Record<string, string>>): number {
  return questions.filter((q) => q.required && !(answers[questionKey(q)] ?? '').trim()).length;
}

// ── Search results (board 9: the standard-list fallback) ────────────────────────────────────────

export interface SearchRowView {
  id: string;
  name: string;
  subLine: string;
  /** Right side: the default lab ("NRL") or null when the row carries the Patient's choice tag. */
  labTag: string | null;
  /** The default lab's full name, for the tooltip on `labTag`. */
  labTitle: string | null;
  patientsChoice: boolean;
}

export function searchRowView(item: LabOrderSearchItem): SearchRowView {
  const pc = !!item.fromStandardList || !!item.isPatientsChoice || !item.defaultLab;
  if (item.fromStandardList) {
    const loinc = item.loinc?.trim();
    return {
      id: item.id,
      name: item.name,
      subLine: [loinc ? `LOINC ${loinc}` : item.clinicCode?.trim(), NOT_OFFERED_BY_CLINIC].filter(Boolean).join(' · '),
      labTag: null,
      labTitle: null,
      patientsChoice: true,
    };
  }
  const lab = labName(item.defaultLab);
  return {
    id: item.id,
    name: item.name,
    subLine: [item.clinicCode?.trim(), typeLabel(item.type)].filter(Boolean).join(' · '),
    labTag: pc ? null : lab || null,
    labTitle: pc ? null : labTitle(item.defaultLab) || null,
    patientsChoice: pc && !isClinicPanelType(item.type),
  };
}
